// Analytics -> Reporting: a two-pane workspace (owner, 2026-11-04 — D-461,
// chosen from three pitches: "Let's take the C approach for Reporting").
//
// The left rail, Reports, lists every chart by section, searchable, each row
// ending in a pin; up to five pinned reports also head the rail under Pinned
// (D-470; the P key pins the focused row, `lib/reportPins.ts`). The right
// pane fills the rest of the window: a header strip (the title, the active
// filters when there are any, then Compare, the Summary / Invoices / Recent
// Reports switch and Download) over the body. Compare draws a second chart
// beside the first.
//
// NO EXPLAINER TEXT (owner, 2026-11-04): the intro line, the chart count, the
// computed one-line summary, each chart's leading figure in the rail, "All
// invoices you can see" and Clear Filters were all dropped.
//
// What D-460 asked of Reporting holds: all 30 charts (`lib/reportCharts.ts`),
// the Filters modal's criteria, a bar click toggling its own filter, every
// matching invoice in the listing, and Download as PDF or Excel with the
// invoices' documents (`lib/chartExport.ts`). Every read here runs under the
// Reporting lens (D-258).
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import {
  Body1,
  Button,
  Caption1,
  SearchBox,
  Spinner,
  Subtitle1,
  Subtitle2,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Toast,
  ToastTitle,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
  useToastController,
} from '@fluentui/react-components'
import { FilterRegular, PinFilled, PinRegular } from '@fluentui/react-icons'
import { errorMessage } from '../api/errors'
import {
  REPORT_INVOICE_CAP,
  type AnalyticsParams,
  type ReportCriteria,
  type ReportInvoiceRow,
  type ReportRequestSummary,
} from '../api/types'
import { HBarList, type BarRowData } from '../components/DashboardCharts'
import { InvoiceListing } from '../components/InvoiceListing'
import { OverTimeView } from '../components/OverTimeView'
import { OVER_TIME_CHARTS, overTimeChartById } from '../lib/overTimeCharts'
import { REPORTING_CHARTS, isLineItemsChart, reportingChartById, withGroupRows } from '../lib/codeCharts'
import { entityReportCharts } from '../lib/entityCharts'
import { useEntityChartData } from '../lib/entityChartData'
import { SegmentedControl } from '../components/SegmentedControl'
import { CompareDialog, type Comparison } from '../components/CompareDialog'
import { ReportExportDialog, type ExportChoice } from '../components/ReportExportDialog'
import { ErrorBanner, Loading } from '../components/States'
import { TOASTER_ID } from '../lib/constants'
import {
  FLAG_OPTIONS,
  STATUS_STORED_COUNT,
  criteriaCount,
  statusSelectionForNode,
  toAnalyticsParams,
  toInvoiceListParams,
  toReportCriteria,
  toggleValues,
} from '../lib/criteria'
import { useCriteriaOptions, useVendorNames } from '../lib/criteriaOptions'
import {
  createReport,
  getReportContents,
  getUploadPdfUrl,
  listReports,
  loadAnalyticsByDimension,
  loadAnalyticsCorrections,
  loadAnalyticsLineItems,
  loadAnalyticsSpendByCode,
  loadAnalyticsSpendByEntity,
  loadDashboardInvoices,
} from '../lib/data'
import type { Invoice } from '../lib/types'
import { describeReportCriteria } from '../lib/reportCriteria'
import {
  DEFAULT_CHART_ID,
  NO_KEY,
  type ChartData,
  type ReportChart,
} from '../lib/reportCharts'
import { appendPdf, buildChartPdf, buildChartWorkbook, exportFileName, saveBytes } from '../lib/chartExport'
import { ReportAssemblyError, assembleReport, runOnPhrase, saveReport, type AssembleProgress } from '../lib/reportPdf'
import { omittedCompanyFlagLines } from '../lib/reportLayout'
import { MAX_PINS, railMatches, useReportPins } from '../lib/reportPins'
import { SECTION_COLORS } from '../lib/theme'
import type { Criteria } from '../lib/criteria'
import type { Session } from '../lib/identity'

/** The most invoices a Download lists. The screen pages; a file cannot. */
const EXPORT_LISTING_CAP = 5000
const EXPORT_PAGE_SIZE = 200

/** Every report the rail offers, section by section: the bar charts, then Over
    Time's line charts (owner, 2026-11-04 — D-464). The bar charts include Per
    Account and Per Department (D-465), straight after Per Company; Associated
    Entities comes last (D-466). */
const RAIL_ENTRIES: readonly { id: string; title: string; group: string }[] = [
  ...REPORTING_CHARTS,
  ...OVER_TIME_CHARTS,
  ...entityReportCharts(null),
]

/** The Associated Entities reports' ids: their rows come from their own read (D-466). */
const ENTITY_IDS = new Set(entityReportCharts(null).map((c) => c.id))

const CHART_GROUPS = [...new Set(RAIL_ENTRIES.map((chart) => chart.group))]

/** Every id a pin may name: a stored pin outside this set is dropped (D-470). */
const RAIL_IDS: ReadonlySet<string> = new Set(RAIL_ENTRIES.map((c) => c.id))

/** The Pinned section's heading, matched by the rail's search as any section's is. */
const PINNED_SECTION = 'Pinned'

/** The disabled pin's tooltip once five are pinned. */
const PINS_FULL_TIP = 'Five reports are pinned, so unpin one first.'

/** One row of the rail. A pinned report has two: one in Pinned and one in its own section. */
interface RailRow {
  key: string
  id: string
  title: string
  inPinned: boolean
}

/** A colour per rail section, the same hues the bars are drawn in. */
const GROUP_COLOR: Record<string, string> = {
  'Per Step': SECTION_COLORS.step,
  'Per Vendor': SECTION_COLORS.vendor,
  'Per Company': SECTION_COLORS.company,
  'Per Account': SECTION_COLORS.spend,
  'Per Department': SECTION_COLORS.spend,
  'Line Items': SECTION_COLORS.spend,
  'By Spend': SECTION_COLORS.spend,
  'Corrections, Flags, and Holds': SECTION_COLORS.feedback,
  'Over Time': SECTION_COLORS.overTime,
  'Associated Entities': SECTION_COLORS.spend,
}

type WorkspaceTab = 'chart' | 'invoices' | 'recent'

/** The shortest the workspace gets; below it the page scrolls as a whole. */
const MIN_HEIGHT = 480

const useStyles = makeStyles({
  // Height is set from the window on mount and resize, so the page is one
  // screen tall and only the panes scroll.
  root: {
    display: 'grid',
    gridTemplateColumns: '256px minmax(0, 1fr)',
    gap: '16px',
    paddingTop: '8px',
    minHeight: `${MIN_HEIGHT}px`,
  },
  // ---- The rail ----
  rail: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflow: 'hidden',
  },
  railHead: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '14px 14px 10px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  muted: { color: tokens.colorNeutralForeground3 },
  search: { width: '100%', maxWidth: 'none' },
  railList: { flexGrow: 1, overflowY: 'auto', padding: '6px 6px 12px', outlineStyle: 'none' },
  groupHead: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 10px 4px',
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: tokens.colorNeutralForeground3,
  },
  dot: { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
  groupIcon: { fontSize: '12px', flexShrink: 0, color: tokens.colorNeutralForeground3 },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 4px 4px 12px',
    minHeight: '34px',
    boxSizing: 'border-box',
    borderRadius: tokens.borderRadiusMedium,
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: 'transparent',
    cursor: 'pointer',
    outlineStyle: 'none',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '-2px',
    },
  },
  itemSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    borderLeftColor: tokens.colorCompoundBrandStroke,
    ':hover': { backgroundColor: tokens.colorBrandBackground2Hover },
  },
  itemCompared: { borderLeftColor: tokens.colorNeutralStroke1 },
  itemTitle: {
    flexGrow: 1,
    minWidth: 0,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  itemTitleSelected: { color: tokens.colorBrandForeground1, fontWeight: tokens.fontWeightSemibold },
  // The pin at the end of each row (D-470): outline until pinned, then filled.
  pin: {
    flexShrink: 0,
    minWidth: '26px',
    width: '26px',
    height: '26px',
    padding: 0,
    color: tokens.colorNeutralForeground3,
  },
  pinOn: { color: tokens.colorBrandForeground1 },
  noMatch: { padding: '24px 12px', color: tokens.colorNeutralForeground3 },
  // ---- The workspace ----
  workspace: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflow: 'hidden',
  },
  strip: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px 20px 12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  stripText: { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0, flexGrow: 1 },
  eyebrow: { display: 'flex', alignItems: 'center', gap: '6px', color: tokens.colorNeutralForeground3 },
  stripTitle: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  filterRow: { display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, color: tokens.colorNeutralForeground3 },
  filterText: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 },
  stripActions: { display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 },
  body: { flexGrow: 1, minHeight: 0, overflowY: 'auto', padding: '16px 20px 20px' },
  hidden: { display: 'none' },
  // ---- The chart panes ----
  panes: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '16px', minHeight: '100%' },
  panesCompare: { gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' },
  pane: { display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0 },
  paneCompared: {
    paddingLeft: '16px',
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  paneHead: { display: 'flex', flexDirection: 'column', gap: '4px' },
  paneFilters: { color: tokens.colorNeutralForeground3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  hint: { color: tokens.colorNeutralForeground3, marginTop: '4px' },
  // ---- Recent reports ----
  criteriaCell: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  criteriaText: { display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  downloadHead: { width: '130px' },
  downloadCell: { whiteSpace: 'nowrap' },
  runCell: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  byCell: { overflow: 'hidden', textOverflow: 'ellipsis' },
  runHead: { width: '260px' },
  byHead: { width: '170px' },
  invoicesHead: { width: '80px' },
})

/** The criteria as a file should print them: only the ones that narrow. Copied from `ReportingScreen.tsx`. */
function limitingCriteria(
  criteria: ReportCriteria,
  totals: { companies: number; accounts: number; departments: number; codes: number },
): ReportCriteria {
  const out = { ...criteria }
  const dropIfWhole = (key: keyof ReportCriteria, total: number): void => {
    const picked = out[key]
    if (Array.isArray(picked) && total > 0 && picked.length >= total) delete out[key]
  }
  dropIfWhole('company_id', totals.companies)
  dropIfWhole('account_id', totals.accounts)
  dropIfWhole('department_id', totals.departments)
  dropIfWhole('code_id', totals.codes)
  dropIfWhole('status', STATUS_STORED_COUNT)
  dropIfWhole('flag_type', FLAG_OPTIONS.length)
  dropIfWhole('flag_type_any', FLAG_OPTIONS.length)
  return out
}

/** The nine reads every chart draws from, for one set of filters. */
async function readChartData(params: AnalyticsParams): Promise<{ data: ChartData; error: string | null }> {
  const [step, vendor, company, accounts, departments, codes, entities, corrections, lineItems] = await Promise.allSettled([
    loadAnalyticsByDimension('step', params),
    loadAnalyticsByDimension('vendor', params),
    loadAnalyticsByDimension('company', params),
    loadAnalyticsSpendByCode('account', params),
    loadAnalyticsSpendByCode('department', params),
    loadAnalyticsSpendByCode('code', params),
    loadAnalyticsSpendByEntity(params),
    loadAnalyticsCorrections(params),
    loadAnalyticsLineItems(params),
  ])
  const settled = <T,>(result: PromiseSettledResult<T>): T | null => (result.status === 'fulfilled' ? result.value : null)
  const error =
    step.status === 'rejected' && vendor.status === 'rejected' && company.status === 'rejected'
      ? errorMessage(step.reason)
      : null
  return {
    error,
    data: {
      step: settled(step),
      vendor: settled(vendor),
      company: settled(company),
      accounts: settled(accounts),
      departments: settled(departments),
      codes: settled(codes),
      entities: settled(entities),
      corrections: settled(corrections),
      lineItems: settled(lineItems),
    },
  }
}

/** The chart data for one set of filters, re-read when they or the refresh change. `null` params read nothing. */
function useChartData(params: AnalyticsParams | null, refreshNonce: number) {
  const [data, setData] = useState<ChartData | null>(null)
  const [loading, setLoading] = useState(params !== null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (params === null) {
      setData(null)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    void readChartData(params).then((result) => {
      if (cancelled) return
      setData(result.data)
      setError(result.error)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [params, refreshNonce])
  return { data, loading, error }
}

interface Props {
  session: Session
  refreshNonce?: number
  /** The Filters modal's criteria, held by `AnalyticsScreen.tsx`. */
  criteria: Criteria
  onCriteriaChange: (next: Criteria) => void
  /** From `App.tsx`: where an invoice opened from the listing is being worked. */
  resolveStep?: (invoice: Invoice) => { label: string; onGo: () => void } | null
}

export function ReportingScreen({
  session,
  refreshNonce = 0,
  criteria: form,
  onCriteriaChange: setForm,
  resolveStep,
}: Props) {
  const s = useStyles()
  const { dispatchToast } = useToastController(TOASTER_ID)
  const toast = useCallback(
    (message: string, intent: 'success' | 'info' | 'error') =>
      dispatchToast(
        <Toast>
          <ToastTitle>{message}</ToastTitle>
        </Toast>,
        { intent },
      ),
    [dispatchToast],
  )

  const [chartId, setChartId] = useState(DEFAULT_CHART_ID)
  // SHOW GROUP ROWS (owner, 2026-11-04 — D-465: "when we are offering visuals
  // detailing line items, we should offer the Show Groups toggle"): the line
  // table's own switch and label. On, a group counts once; off, each line.
  const [showGroupRows, setShowGroupRows] = useState(true)
  const analyticsParams = useMemo(() => toAnalyticsParams(form), [form])
  // ASSOCIATED ENTITIES (D-466) reads its own endpoint, for each side of a
  // filter comparison; its charts carry their rows with them.
  const entity = useEntityChartData(ENTITY_IDS.has(chartId) ? analyticsParams : null, refreshNonce)
  const entityChart = ENTITY_IDS.has(chartId) ? entityReportCharts(entity.data).find((c) => c.id === chartId) : undefined
  const chart = entityChart ?? withGroupRows(reportingChartById(chartId), showGroupRows)
  /** The Over Time report on screen, when it is one: drawn by `OverTimeView`. */
  const overTime = overTimeChartById(chartId)
  /** The report on screen, whichever catalogue it is from: its title, section and headings. */
  const current = overTime ?? chart
  // Over Time's rows for Download, handed up by its view.
  const [overTimeRows, setOverTimeRows] = useState<{ rows: BarRowData[] | null; truncated: boolean }>({
    rows: null,
    truncated: false,
  })
  // COMPARE (owner, 2026-11-04 — D-463): the same report under other filters,
  // or another report under the same filters, side by side.
  const [comparison, setComparison] = useState<Comparison | null>(null)
  const [compareOpen, setCompareOpen] = useState(false)
  const comparing = comparison !== null
  // A report compared with itself is no comparison: picking it in the rail ends it.
  const compareId = comparison?.mode === 'reports' && comparison.chartId !== chartId ? comparison.chartId : null
  const [tab, setTab] = useState<WorkspaceTab>('chart')
  const [query, setQuery] = useState('')

  const options = useCriteriaOptions(session.analyticsCompanies.reporting)
  // Every vendor the filters name gets its name, however it was picked.
  const vendorNames = useVendorNames(
    comparison?.mode === 'filters' ? [...form.vendors, ...comparison.criteria.vendors] : form.vendors,
  )
  const labels = useMemo(
    () => ({ ...options.labels, vendors: { ...options.labels.vendors, ...vendorNames } }),
    [options.labels, vendorNames],
  )

  const criteriaLines = useMemo(
    () =>
      describeReportCriteria(
        limitingCriteria(toReportCriteria(form), {
          companies: options.companies.length,
          accounts: options.accounts.length,
          departments: options.departments.length,
          codes: options.codes.length,
        }),
        labels,
      ),
    [form, options, labels],
  )

  // ---- Fill the window ------------------------------------------------------------
  // THE PAGE IS EXACTLY ONE WINDOW TALL (owner, 2026-11-04: "The screen is
  // scrollable beyond the shown content"). The first cut subtracted a guessed
  // margin and left the page ~100px too tall, because the shell pads below the
  // screen. This sets a first height, then takes off whatever the document
  // still overflows by, so the shell's own padding is measured, not assumed.
  const rootRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const fit = () => {
      const el = rootRef.current
      if (el === null) return
      const top = el.getBoundingClientRect().top + window.scrollY
      let height = Math.max(MIN_HEIGHT, window.innerHeight - top)
      el.style.height = `${height}px`
      const overflow = document.documentElement.scrollHeight - window.innerHeight
      if (overflow > 0) {
        height = Math.max(MIN_HEIGHT, height - overflow)
        el.style.height = `${height}px`
      }
    }
    fit()
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [])

  // ---- The chart reads (one set feeds all 30 charts) ---------------------------------
  const { data, loading, error } = useChartData(analyticsParams, refreshNonce)
  // The second side's own reads, only when it compares other filters.
  const compareParams = useMemo(
    () => (comparison?.mode === 'filters' ? toAnalyticsParams(comparison.criteria) : null),
    [comparison],
  )
  const other = useChartData(compareParams, refreshNonce)
  const otherEntity = useEntityChartData(ENTITY_IDS.has(chartId) ? compareParams : null, refreshNonce)

  const rows = data === null ? null : chart.rows(data)
  const truncated = data !== null && chart.truncated(data)

  /** A bar is a filter control: a click toggles its own value. */
  const selectFor = useCallback(
    (target: ReportChart) => {
      const key = target.filter
      if (key === undefined) return undefined
      return (barKey: string) => {
        if (barKey === NO_KEY) return
        const values = key === 'statuses' ? statusSelectionForNode(barKey) : [barKey]
        setForm({ ...form, [key]: toggleValues(form[key], values) })
      }
    },
    [form, setForm],
  )

  // ---- The rail ----------------------------------------------------------------------
  const visible = useMemo(() => RAIL_ENTRIES.filter((c) => railMatches(c, query)), [query])
  // PINNED (owner, 2026-11-04 — D-470): up to five reports, kept per person on
  // the server, listed first in the order they were pinned. Search filters
  // them as it filters every other section.
  const onPinError = useCallback((message: string) => toast(message, 'error'), [toast])
  const { pins, ready: pinsReady, toggle: togglePinned } = useReportPins(RAIL_IDS, onPinError)
  const pinsFull = pins.length >= MAX_PINS
  const pinnedVisible = useMemo(
    () =>
      pins
        .map((id) => RAIL_ENTRIES.find((c) => c.id === id))
        .filter((c): c is (typeof RAIL_ENTRIES)[number] => c !== undefined && railMatches(c, query, PINNED_SECTION)),
    [pins, query],
  )
  /** Every row in the order the arrow keys walk it: Pinned first, then each section. */
  const railRows = useMemo<RailRow[]>(
    () => [
      ...pinnedVisible.map((c) => ({ key: `pinned:${c.id}`, id: c.id, title: c.title, inPinned: true })),
      ...CHART_GROUPS.flatMap((group) =>
        visible.filter((c) => c.group === group).map((c) => ({ key: c.id, id: c.id, title: c.title, inPinned: false })),
      ),
    ],
    [pinnedVisible, visible],
  )
  const itemRefs = useRef(new Map<string, HTMLDivElement>())
  // The row last chosen, so a report shown twice is walked from the copy in use.
  const [activeRow, setActiveRow] = useState<string | null>(null)
  const activeIndex = (() => {
    const exact = railRows.findIndex((r) => r.key === activeRow && r.id === chartId)
    return exact >= 0 ? exact : railRows.findIndex((r) => r.id === chartId)
  })()
  const tabStopKey = railRows[activeIndex >= 0 ? activeIndex : 0]?.key ?? null
  // A row that vanished while focused (unpinned from Pinned) hands focus to the report's own row.
  const pendingFocus = useRef<string | null>(null)
  useEffect(() => {
    if (pendingFocus.current === null) return
    itemRefs.current.get(pendingFocus.current)?.focus()
    pendingFocus.current = null
  })

  const choose = (row: RailRow, focus: boolean) => {
    setChartId(row.id)
    setActiveRow(row.key)
    setTab('chart')
    if (focus) itemRefs.current.get(row.key)?.focus()
  }

  /** Pins or unpins a report from a row, without choosing it. */
  const pinFrom = (row: RailRow, viaKeyboard: boolean) => {
    const changed = togglePinned(row.id)
    if (!changed) {
      if (viaKeyboard && pinsReady && pinsFull) toast(PINS_FULL_TIP, 'info')
      return
    }
    if (row.inPinned) {
      if (activeRow === row.key) setActiveRow(row.id)
      if (viaKeyboard) pendingFocus.current = row.id
    }
  }

  const onRailKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (railRows.length === 0) return
    const at = activeIndex
    let next: number | null = null
    if (event.key === 'ArrowDown') next = at < 0 ? 0 : Math.min(railRows.length - 1, at + 1)
    else if (event.key === 'ArrowUp') next = at < 0 ? 0 : Math.max(0, at - 1)
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = railRows.length - 1
    if (next === null) return
    event.preventDefault()
    choose(railRows[next], true)
  }

  /** One rail row: the title, then its pin. */
  const renderRow = (row: RailRow) => {
    const selected = row.id === chartId
    const isCompared = comparing && row.id === compareId
    const pinned = pins.includes(row.id)
    const blocked = !pinned && pinsFull
    return (
      <div
        key={row.key}
        ref={(el) => {
          if (el === null) itemRefs.current.delete(row.key)
          else itemRefs.current.set(row.key, el)
        }}
        role="option"
        aria-selected={selected}
        aria-label={pinned ? `${row.title}, pinned` : row.title}
        aria-keyshortcuts="P"
        // One tab stop for the whole list: the row in use, or the first one shown.
        tabIndex={row.key === tabStopKey ? 0 : -1}
        className={mergeClasses(s.item, isCompared && s.itemCompared, selected && s.itemSelected)}
        onClick={() => choose(row, false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            choose(row, false)
          } else if ((e.key === 'p' || e.key === 'P') && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault()
            pinFrom(row, true)
          }
        }}
      >
        <span className={mergeClasses(s.itemTitle, selected && s.itemTitleSelected)} title={row.title}>
          {row.title}
        </span>
        {pinsReady && (
          <Tooltip content={blocked ? PINS_FULL_TIP : pinned ? 'Unpin' : 'Pin'} relationship={blocked ? 'description' : 'label'}>
            <Button
              appearance="transparent"
              size="small"
              className={mergeClasses(s.pin, pinned && s.pinOn)}
              aria-label={pinned ? 'Unpin' : 'Pin'}
              aria-pressed={pinned}
              icon={pinned ? <PinFilled /> : <PinRegular />}
              disabledFocusable={blocked}
              // Reached by the P key on the row, so the list keeps its one tab stop.
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation()
                pinFrom(row, false)
              }}
            />
          </Tooltip>
        )}
      </div>
    )
  }

  // ---- The listing's count -----------------------------------------------------------
  const [listTotal, setListTotal] = useState<number | null>(null)

  // ---- Recent reports ----------------------------------------------------------------
  const [recent, setRecent] = useState<ReportRequestSummary[]>([])
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [recentError, setRecentError] = useState<string | null>(null)
  const loadRecent = useCallback(async () => {
    try {
      setRecent(await listReports())
    } catch {
      // The audit list is context, not the feature.
    }
  }, [])
  useEffect(() => {
    void loadRecent()
  }, [loadRecent, refreshNonce])

  // ---- Downloading (copied from `ReportingScreen.tsx`) ---------------------------------
  const [exportOpen, setExportOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [step, setStep] = useState<string | null>(null)
  const [progress, setProgress] = useState<AssembleProgress | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)

  const fetchUpload = useCallback(async (uploadId: string) => {
    for (let attempt = 1; ; attempt++) {
      try {
        const { url } = await getUploadPdfUrl(uploadId)
        const response = await fetch(url)
        if (!response.ok) throw new Error(`The document answered ${response.status}.`)
        return new Uint8Array(await response.arrayBuffer())
      } catch (e) {
        if (attempt >= 2) throw e
      }
    }
  }, [])

  const assemble = useCallback(
    (args: {
      invoices: readonly ReportInvoiceRow[]
      criteria: ReportCriteria
      requestedBy: string
      requestedAt: string
      downloadedAt?: string
      omittedCompanyFlagCount?: number
    }) =>
      assembleReport({
        invoices: args.invoices,
        criteriaLines: describeReportCriteria(
          limitingCriteria(args.criteria, {
            companies: options.companies.length,
            accounts: options.accounts.length,
            departments: options.departments.length,
            codes: options.codes.length,
          }),
          labels,
        ),
        requestedBy: args.requestedBy,
        requestedAt: args.requestedAt,
        downloadedAt: args.downloadedAt,
        omittedCompanyFlagCount: args.omittedCompanyFlagCount,
        onProgress: setProgress,
        fetchUpload,
      }),
    [options, labels, fetchUpload],
  )

  const download = async ({ format, includeInvoices }: ExportChoice): Promise<void> => {
    setBusy(true)
    setExportError(null)
    setProgress(null)
    const at = new Date()
    try {
      setStep('Gathering the invoice listing…')
      const listParams = toInvoiceListParams(form)
      const invoices: Invoice[] = []
      let cursor: string | undefined
      let total: number | null = null
      do {
        const page = await loadDashboardInvoices(listParams, cursor, EXPORT_PAGE_SIZE)
        invoices.push(...page.rows)
        total = page.total ?? total
        cursor = page.cursor
      } while (cursor !== undefined && invoices.length < EXPORT_LISTING_CAP)
      const listed = invoices.slice(0, EXPORT_LISTING_CAP)

      let documents: Awaited<ReturnType<typeof assembleReport>> | null = null
      let note = ''
      if (includeInvoices) {
        setStep("Selecting the invoices' documents…")
        const report = await createReport(toReportCriteria(form))
        void loadRecent()
        if (report.invoice_count === 0) {
          note = [' No invoices matched, so no documents were added.', ...omittedCompanyFlagLines(report.omitted_company_flag_count)].join(' ')
        } else {
          setStep("Assembling the invoices' documents…")
          documents = await assemble({
            invoices: report.invoices,
            criteria: report.criteria,
            requestedBy: session.user.fullName,
            requestedAt: report.requested_at,
            omittedCompanyFlagCount: report.omitted_company_flag_count,
          })
          const missing = documents.missingPageCount
          if (missing > 0) note = ` Source pages are missing for ${missing === 1 ? 'one invoice' : `${missing} invoices`}, as the cover explains.`
        }
      }

      setStep('Writing the file…')
      setProgress(null)
      const input = {
        chart: current,
        rows: (overTime !== undefined ? overTimeRows.rows : rows) ?? [],
        truncated: overTime !== undefined ? overTimeRows.truncated : truncated,
        criteriaLines,
        invoices: listed,
        invoiceTotal: total,
        requestedBy: session.user.fullName,
        at,
      }
      const fileName = exportFileName(current, format, at)
      if (format === 'pdf') {
        let bytes = await buildChartPdf(input)
        if (documents !== null) bytes = await appendPdf(bytes, documents.bytes)
        saveBytes(bytes, fileName, 'application/pdf')
        toast(`Downloaded ${fileName}.${note}`, 'success')
      } else {
        saveBytes(buildChartWorkbook(input), fileName, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
        if (documents !== null) saveReport(documents)
        toast(
          documents === null
            ? `Downloaded ${fileName}.${note}`
            : `Downloaded ${fileName}, and the invoices' documents as ${documents.fileName}.${note}`,
          'success',
        )
      }
      setExportOpen(false)
    } catch (e) {
      setExportError(e instanceof ReportAssemblyError ? e.message : errorMessage(e))
    } finally {
      setBusy(false)
      setStep(null)
      setProgress(null)
    }
  }

  const downloadAgain = async (entry: ReportRequestSummary): Promise<void> => {
    setDownloadingId(entry.report_id)
    setRecentError(null)
    try {
      const contents = await getReportContents(entry.report_id)
      if (contents.invoices.length === 0) {
        toast('That report covered no invoices, so there is nothing to download.', 'info')
        return
      }
      const assembled = await assemble({
        invoices: contents.invoices,
        criteria: contents.criteria,
        requestedBy: contents.requested_by_name ?? contents.requested_by,
        requestedAt: contents.requested_at,
        downloadedAt: new Date().toISOString(),
      })
      saveReport(assembled)
      toast(`Your report was downloaded as ${assembled.fileName}.`, 'success')
    } catch (e) {
      setRecentError(e instanceof ReportAssemblyError ? e.message : errorMessage(e))
    } finally {
      setDownloadingId(null)
      setProgress(null)
    }
  }

  const progressValue = progress !== null && progress.total > 0 ? progress.done / progress.total : null
  const progressLabel =
    progress === null
      ? step
      : progress.phase === 'fetching'
        ? `Fetching document ${progress.done} of ${progress.total}…`
        : `Assembling page ${progress.done} of ${progress.total}…`

  // ---- One chart pane ----------------------------------------------------------------
  /** The filters as a sentence, for a pane's heading when filters are compared. */
  const describe = (criteria: Criteria): string =>
    criteriaCount(criteria) === 0
      ? 'No filters'
      : describeReportCriteria(
          limitingCriteria(toReportCriteria(criteria), {
            companies: options.companies.length,
            accounts: options.accounts.length,
            departments: options.departments.length,
            codes: options.codes.length,
          }),
          labels,
        ).join(' · ')

  /** An Over Time pane: its line chart, under the side's own filters. */
  const renderOverTimePane = (id: string, side: 'main' | 'other') => {
    const criteria = side === 'other' && comparison?.mode === 'filters' ? comparison.criteria : form
    const heading = comparison?.mode === 'filters' ? describe(criteria) : null
    return (
      <div className={mergeClasses(s.pane, side === 'other' && s.paneCompared)}>
        {comparison?.mode === 'filters' && (
          <div className={s.paneHead}>
            <Subtitle2>{side === 'main' ? 'Current Filters' : 'Compared Filters'}</Subtitle2>
            {heading !== null && (
              <Tooltip content={heading} relationship="description">
                <Caption1 className={s.paneFilters}>{heading}</Caption1>
              </Tooltip>
            )}
          </div>
        )}
        <OverTimeView
          chartId={id}
          criteria={criteria}
          refreshNonce={refreshNonce}
          onRows={side === 'main' ? (r, t) => setOverTimeRows({ rows: r, truncated: t }) : undefined}
        />
      </div>
    )
  }

  const renderPane = (target: ReportChart, side: 'main' | 'other') => {
    const otherFilters = side === 'other' && comparison?.mode === 'filters'
    const base = otherFilters ? other : { data, loading }
    // An Associated Entities chart draws from its own read, the other side's when compared.
    const own = ENTITY_IDS.has(target.id) ? (otherFilters ? otherEntity : entity) : null
    if (own !== null) target = entityReportCharts(own.data).find((c) => c.id === target.id) ?? target
    const source = { data: base.data, loading: base.loading || (own !== null && own.loading && own.data === null) }
    const paneRows = source.data === null ? null : target.rows(source.data)
    const paneTruncated = source.data !== null && target.truncated(source.data)
    // A bar click edits the view's own filters, so only the main side's bars are controls.
    const onSelect = side === 'main' ? selectFor(target) : undefined
    const heading =
      comparison?.mode === 'filters'
        ? describe(side === 'main' ? form : comparison.criteria)
        : null
    return (
      <div className={mergeClasses(s.pane, side === 'other' && s.paneCompared)}>
        {comparing && (
          <div className={s.paneHead}>
            <Subtitle2>{comparison?.mode === 'filters' ? (side === 'main' ? 'Current Filters' : 'Compared Filters') : target.title}</Subtitle2>
            {heading !== null && (
              <Tooltip content={heading} relationship="description">
                <Caption1 className={s.paneFilters}>{heading}</Caption1>
              </Tooltip>
            )}
          </div>
        )}
        {source.loading && source.data === null ? (
          <Loading label="Loading the chart…" />
        ) : paneRows === null ? (
          <Body1 className={s.muted}>This chart could not be loaded.</Body1>
        ) : paneRows.length === 0 ? (
          <Body1 className={s.muted}>Nothing to show for these filters.</Body1>
        ) : (
          <>
            <HBarList
              rows={paneRows}
              onSelect={onSelect}
              labelWidth={comparing ? 170 : 280}
              valueWidth={target.valueWidth ?? (comparing ? 80 : 96)}
            />
            {paneTruncated && (
              <Caption1 className={s.hint}>Some rows with small numbers on every measure are not shown.</Caption1>
            )}
          </>
        )}
      </div>
    )
  }

  const filterSentence = criteriaLines.join(' · ')

  return (
    <div ref={rootRef} className={s.root}>
      {/* ---- The rail ---- */}
      <nav className={s.rail} aria-label="Reports">
        <div className={s.railHead}>
          <Subtitle2>Reports</Subtitle2>
          <SearchBox
            className={s.search}
            aria-label="Search the reports"
            placeholder="Search the reports"
            value={query}
            onChange={(_, d) => setQuery(d.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' && railRows.length > 0) {
                e.preventDefault()
                choose(railRows[activeIndex >= 0 ? activeIndex : 0], true)
              }
            }}
          />
        </div>
        <div className={s.railList} role="listbox" aria-label="Reports" onKeyDown={onRailKey}>
          {railRows.length === 0 && <Body1 className={s.noMatch}>No report matches that search.</Body1>}
          {/* PINNED first, and only when something pinned matches (D-470). */}
          {pinnedVisible.length > 0 && (
            <div role="group" aria-label={PINNED_SECTION}>
              <div className={s.groupHead} aria-hidden>
                <PinFilled className={s.groupIcon} />
                {PINNED_SECTION}
              </div>
              {railRows.filter((r) => r.inPinned).map(renderRow)}
            </div>
          )}
          {CHART_GROUPS.map((group) => {
            const inGroup = visible.filter((c) => c.group === group)
            if (inGroup.length === 0) return null
            return (
              <div key={group} role="group" aria-label={group}>
                <div className={s.groupHead} aria-hidden>
                  <span className={s.dot} style={{ backgroundColor: GROUP_COLOR[group] ?? tokens.colorBrandBackground }} />
                  {group}
                </div>
                {inGroup.map((c) => renderRow({ key: c.id, id: c.id, title: c.title, inPinned: false }))}
              </div>
            )
          })}
        </div>
      </nav>

      {/* ---- The workspace ---- */}
      <section className={s.workspace} aria-label={current.title}>
        <div className={s.strip}>
          <div className={s.stripText}>
            <Caption1 className={s.eyebrow}>
              <span className={s.dot} style={{ backgroundColor: GROUP_COLOR[current.group] ?? tokens.colorBrandBackground }} />
              {current.group}
            </Caption1>
            <Subtitle1 className={s.stripTitle} title={current.title}>
              {compareId !== null ? `${current.title} and ${reportingChartById(compareId).title}` : current.title}
            </Subtitle1>
            {criteriaCount(form) > 0 && (
              <div className={s.filterRow}>
                <FilterRegular aria-hidden />
                <Tooltip content={filterSentence} relationship="description">
                  <Caption1 className={s.filterText}>{filterSentence}</Caption1>
                </Tooltip>
              </div>
            )}
          </div>
          <div className={s.stripActions}>
            {/* Only while a line-item report is on screen, on either side. */}
            {(isLineItemsChart(chart) || (compareId !== null && isLineItemsChart(reportingChartById(compareId)))) && (
              <Switch
                labelPosition="before"
                label="Show Group Rows"
                checked={showGroupRows}
                onChange={(_, d) => setShowGroupRows(d.checked)}
              />
            )}
            {/* THE SHARED SEGMENTED CONTROL beside Download (owner, 2026-11-04),
                not a tab row: the header's tabs are the page's only one. */}
            <SegmentedControl<WorkspaceTab>
              ariaLabel="Workspace views"
              value={tab}
              onChange={setTab}
              options={[
                { value: 'chart', label: 'Summary' },
                { value: 'invoices', label: listTotal === null ? 'Invoices' : `Invoices (${listTotal})` },
                ...(recent.length > 0 ? [{ value: 'recent' as const, label: 'Recent Reports' }] : []),
              ]}
            />
            {/* COMPARE, just left of Download (owner, 2026-11-04). Filled while
                a comparison is on screen, as the Filters icon is while filters are.
                NO ICONS on either (owner, 2026-11-04: "No other buttons have
                icons, so I am not sure why these got them"). */}
            <Button
              appearance={comparing ? 'primary' : 'secondary'}
              onClick={() => setCompareOpen(true)}
            >
              Compare
            </Button>
            <Button
              appearance="primary"
              onClick={() => {
                setExportError(null)
                setExportOpen(true)
              }}
            >
              Download
            </Button>
          </div>
        </div>

        <div className={s.body}>
          <ErrorBanner message={error} />
          {tab === 'chart' && (
            <div className={mergeClasses(s.panes, (comparison?.mode === 'filters' || compareId !== null) && s.panesCompare)}>
              {overTime !== undefined ? renderOverTimePane(overTime.id, 'main') : renderPane(chart, 'main')}
              {comparison?.mode === 'filters' &&
                (overTime !== undefined ? renderOverTimePane(overTime.id, 'other') : renderPane(chart, 'other'))}
              {compareId !== null && renderPane(withGroupRows(reportingChartById(compareId), showGroupRows), 'other')}
            </div>
          )}
          {/* Kept mounted so the tab's count stays current and the page is kept. */}
          <div className={mergeClasses(tab !== 'invoices' && s.hidden)}>
            <InvoiceListing
              session={session}
              criteria={form}
              refreshNonce={refreshNonce}
              section="reporting"
              resolveStep={resolveStep}
              onTotal={setListTotal}
            />
          </div>
          {tab === 'recent' && (
            <>
              <ErrorBanner message={recentError} />
              <Table size="small" aria-label="Recent reports">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell className={s.downloadHead} aria-label="Download" />
                    <TableHeaderCell className={s.runHead}>Run</TableHeaderCell>
                    <TableHeaderCell className={s.byHead}>By</TableHeaderCell>
                    <TableHeaderCell className={s.invoicesHead}>Invoices</TableHeaderCell>
                    <TableHeaderCell>Criteria</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recent.map((entry) => {
                    const text = describeReportCriteria(entry.criteria, labels).join(' · ')
                    return (
                      <TableRow key={entry.report_id}>
                        <TableCell className={s.downloadCell}>
                          <Button
                            size="small"
                            appearance="secondary"
                            disabled={busy || downloadingId !== null}
                            onClick={() => void downloadAgain(entry)}
                            title="Download these same invoices again"
                          >
                            {downloadingId === entry.report_id ? <Spinner size="tiny" label="Downloading…" /> : 'Download'}
                          </Button>
                        </TableCell>
                        <TableCell className={s.runCell}>{runOnPhrase(entry.requested_at)}</TableCell>
                        <TableCell className={s.byCell} title={entry.requested_by}>
                          {entry.requested_by_name ?? entry.requested_by}
                        </TableCell>
                        <TableCell>{entry.invoice_count}</TableCell>
                        <TableCell className={s.criteriaCell}>
                          <Tooltip content={text} relationship="description">
                            <span className={s.criteriaText}>{text}</span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </div>
      </section>

      <CompareDialog
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        value={comparison}
        chartId={chartId}
        criteria={form}
        companyIds={session.analyticsCompanies.reporting}
        onApply={(next) => {
          setComparison(next)
          setTab('chart')
        }}
      />
      <ReportExportDialog
        open={exportOpen}
        chartTitle={chart.title}
        invoiceCount={listTotal}
        invoiceCap={REPORT_INVOICE_CAP}
        busy={busy}
        progressLabel={progressLabel}
        progress={progressValue}
        error={exportError}
        onDownload={(choice) => void download(choice)}
        onClose={() => setExportOpen(false)}
      />
    </div>
  )
}
